import React, { useEffect, useMemo, useState, useRef, MouseEvent } from 'react';
import { LinkAnalyzer } from '../../services/link-analyzer';
import { blokToComponent } from '../../helpers/blok-to-component';
import { BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { RocheTeaser } from '../../types/global';
import { RocheUITeaser } from '@repo/roche-ui';
import { useTranslation } from '../../hooks/use-translation';
import { StringService } from '../../services/string';
import { handleRocheTeaserClick } from '../../helpers/roche-teaser-tracking';
import { parseAssetSource } from '../../utils/asset';
import { ConditionalWrapper } from '../../helpers/conditional-wrapper';
import { getAltText } from './alt-text-helper';
import { settings } from '../../helpers/roche-video-player-settings';
import { rocheVideoPlayerTracking, TrackedPercentage } from '../../helpers/roche-video-player-tracking';

type RocheTeaserProps = BlokToComponentProps<
  RocheTeaser & {
    page: string;
    teaser_image: { brightcove_id?: string; src?: { filename: string; alt?: string }; srcAltText?: string }[];
    teaser_description: string;
    teaser_overline: string;
    teaser_title: string;
    slotted_secondary_action: any[];
  }
>;

export const RocheTeaserExternal = ({
  blok: {
    teaser_image: teaserImage = [],
    teaser_description: teaserDescription,
    layout,
    teaser_overline: teaserOverline,
    teaser_title: teaserTitle,
    page,
    slotted_secondary_action: slotted = [],
  },
  slot,
  keyForChildren,
  wrapperTag,
  getComponent,
  updateHoistedState,
}: RocheTeaserProps): JSX.Element => {
  const Wrapper = wrapperTag as React.ElementType;
  const [hasUpdatedHoistedState, setHasUpdatedHoistedState] = useState(false);
  const [state, setState] = useState({
    internalLinkUrl: '',
    isExternalLink: false,
    loaded: false,
  });
  const firstTeaserImage = teaserImage?.[0];
  const teaserRef = useRef<HTMLDivElement>(null);
  const readMoreTranslation = useTranslation('read more');
  const playLabel = useTranslation('play');
  const pauseLabel = useTranslation('pause');
  const trackedVideoPercentages = useRef<TrackedPercentage>({});

  useEffect(() => {
    const { internalLinkUrl, isExternalLink } = LinkAnalyzer.analyzeLink(page);
    setState({
      internalLinkUrl,
      isExternalLink,
      loaded: state.loaded,
    });
  }, [page]);

  useEffect(() => {
    if (firstTeaserImage && teaserTitle && page) {
      setState((prevState) => ({
        ...prevState,
        loaded: true,
      }));
      if (!hasUpdatedHoistedState) {
        updateHoistedState?.(1, 1);
        setHasUpdatedHoistedState(true);
      }
    }
  }, [firstTeaserImage, teaserTitle, page]);

  const slottedComponents = useMemo(
    () =>
      slotted.map((component) => {
        if (component.component === 'roche-button') {
          return {
            ...component,
            appearance: 'tertiary',
          };
        } else {
          return {}; // in schema slotted is set to a maximum of 1 child (of type button)
        }
      })[0],
    [slotted, layout],
  );

  const slottedSecondaryButton = useMemo(
    () =>
      blokToComponent(
        {
          getComponent,
          blok: slottedComponents,
        },
        `button`,
      ),
    slottedComponents,
  );
  return (
    <ConditionalWrapper
      key={keyForChildren}
      condition={wrapperTag !== undefined && wrapperTag !== ''}
      wrapper={(child: JSX.Element): JSX.Element => <Wrapper slot={slot}>{child}</Wrapper>}
    >
      <RocheUITeaser
        ref={teaserRef}
        layout={layout}
        teaserTitle={teaserTitle}
        teaserOverline={teaserOverline}
        teaserDescription={teaserDescription}
        teaserImage={firstTeaserImage?.brightcove_id || parseAssetSource(firstTeaserImage?.src?.filename)}
        teaserImageAlt={getAltText(firstTeaserImage)}
        teaserUrl={state.internalLinkUrl ?? `${page}`}
        isExternalLink={state.isExternalLink}
        isSearchResult={slot === 'quick-links'}
        slot={slot}
        loaded={state.loaded}
        primaryButtonText={StringService.capitalize(readMoreTranslation)}
        secondaryButton={slottedSecondaryButton}
        brightcoveSettings={settings.brightcove}
        playLabel={playLabel}
        pauseLabel={pauseLabel}
        onTeaserClick={(e: MouseEvent) =>
          handleRocheTeaserClick(e, teaserTitle, state.internalLinkUrl ?? page, teaserRef)
        }
        onVideoStateChange={(event) => rocheVideoPlayerTracking(event, trackedVideoPercentages, true)}
      />
    </ConditionalWrapper>
  );
};
